import { IconPlus } from '@tabler/icons-react';
import {
    useState, useRef, useMemo, useEffect,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    DndContext, closestCenter, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAuthContext } from 'contexts/AuthContext';
import { NSButton, NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import useToast from 'hooks/useToast';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { useGetTimelines } from './hooks/useGetTimeline';
import { useGetTimelineLatestComments } from './hooks/useGetTimelineLatestComments';
import { useCreateTimeline } from './hooks/useCreateTimeline';
import { useDuplicateTimeline } from './hooks/useDuplicateTimeline';
import { useDeleteTimeline } from './hooks/useDeleteTimeline';
import { useGetTimelineSections } from './hooks/useGetTimelineSections';
import { useUpdateTimelineSection } from './hooks/useUpdateTimelineSection';
import { useDeleteTimelineSection } from './hooks/useDeleteTimelineSection';
import './Timeline.scss';
import 'react-datepicker/dist/react-datepicker.css';
import TimelineActivityRow from './components/TimelineActivityRow/TimelineActivityRow';
import TimelineSectionRow from './components/TimelineSectionRow/TimelineSectionRow';
import { TimelineSlideout } from './components/TimelineSlideout/TimelineSlideout';
import { useSelectedActivity } from './hooks/useSelectedActivity';
import { TimelineItem, TimelineSection } from './types';
import { TimelineStatus } from './constants';
import { ATTACHMENTS_TAB, COMMENTS_TAB, DUE_DILLIGENCE_TIMELINE_SLIDEOUT_TABS } from 'views/DealDashboard/constants';
import invariant from 'helpers/invariant';

const NEW_ACTIVITY_NAME = 'Untitled activity';

type TimelineSectionsOpenState = {
    [userId: string]: {
        [dealId: string]: {
            [sectionId: string]: boolean;
        };
    };
};

const Timeline = ({ dealId }: { dealId: number }) => {
    const { selectedOrganizationId, user } = useAuthContext();
    invariant(selectedOrganizationId, 'selectedOrganizationId is required');
    const queryClient = useQueryClient();
    const { showError } = useToast();

    const listRef = useRef<HTMLDivElement>(null);
    const [editMode, setEditMode] = useState(false);
    const [pendingDeleteTimeline, setPendingDeleteTimeline] = useState<TimelineItem | null>(null);
    const [pendingDeleteSection, setPendingDeleteSection] = useState<TimelineSection | null>(null);
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

    const { data: timelines, isLoading: isLoadingTimelines } = useGetTimelines({
        orgId: selectedOrganizationId, dealId, includeAssignees: true,
    });

    const { data: sections, isLoading: isLoadingSections } = useGetTimelineSections({ orgId: selectedOrganizationId, dealId });

    const timelineData: TimelineItem[] = useMemo(() => timelines ?? [], [timelines]);
    const sectionData: TimelineSection[] = useMemo(() => sections ?? [], [sections]);
    const timelineIds = useMemo(
        () => timelineData.map(t => Number(t.id)).filter(Number.isFinite),
        [timelineData],
    );

    const { data: latestComments } = useGetTimelineLatestComments({
        orgId: selectedOrganizationId,
        dealId,
        timelineIds,
    });

    const ungroupedTimelines = useMemo(
        () => timelineData.filter(item => item.sectionId == null),
        [timelineData],
    );

    const timelinesBySection = useMemo(() => {
        const grouped = new Map<number, TimelineItem[]>();
        timelineData.forEach(item => {
            if (item.sectionId == null) return;
            const sectionItems = grouped.get(item.sectionId) ?? [];
            sectionItems.push(item);
            grouped.set(item.sectionId, sectionItems);
        });
        return grouped;
    }, [timelineData]);

    // Local section order so drag-reorder feels instant; adopts the server order when it changes.
    const sectionIdsFromServer = useMemo(() => sectionData.map(section => section.id), [sectionData]);
    const [orderedSectionIds, setOrderedSectionIds] = useState<number[]>(sectionIdsFromServer);

    useEffect(() => {
        setOrderedSectionIds(prev => {
            if (prev.length === sectionIdsFromServer.length && prev.every((id, index) => id === sectionIdsFromServer[index])) {
                return prev;
            }
            return sectionIdsFromServer;
        });
    }, [sectionIdsFromServer]);

    const orderedSections = useMemo(() => {
        const byId = new Map(sectionData.map(section => [section.id, section]));
        return orderedSectionIds
            .map(id => byId.get(id))
            .filter((section): section is TimelineSection => Boolean(section));
    }, [orderedSectionIds, sectionData]);

    const {
        selectedTimelineId, selectedTimeline, activityTab, openActivity, closeActivity, setActivityTab,
    } = useSelectedActivity(timelineData, isLoadingTimelines);

    const openSlideout = (id: string, tab?: typeof DUE_DILLIGENCE_TIMELINE_SLIDEOUT_TABS[number]) => {
        openActivity(id, tab);
        setEditMode(false);
    };

    const closeSlideout = () => {
        closeActivity();
        setEditMode(false);
    };

    const { mutate: createTimeline, isPending: isCreatingTimeline } = useCreateTimeline();
    const { mutate: duplicateTimeline } = useDuplicateTimeline();
    const { mutate: deleteTimeline } = useDeleteTimeline();
    const { mutate: updateSection, mutateAsync: updateSectionAsync } = useUpdateTimelineSection();
    const { mutate: deleteSection } = useDeleteTimelineSection();

    const invalidateTimelines = () => queryClient.invalidateQueries({
        queryKey: ['timelines', selectedOrganizationId, dealId],
    });

    const invalidateSections = () => queryClient.invalidateQueries({
        queryKey: ['timelineSections', selectedOrganizationId, dealId],
    });

    // Collapse state persisted per user per deal; sections default to expanded.
    const [sectionsOpenState, setSectionsOpenState] = useLocalStorage<TimelineSectionsOpenState>(
        PageSpecificKeys.TimelineSectionsOpen,
        {},
    );
    const userKey = String(user?.id ?? 'anonymous');
    const dealKey = String(dealId);

    const isSectionExpanded = (sectionId: number) => sectionsOpenState[userKey]?.[dealKey]?.[sectionId] ?? true;

    const setSectionExpanded = (sectionId: number, expanded: boolean) => {
        setSectionsOpenState(prev => ({
            ...prev,
            [userKey]: {
                ...prev[userKey],
                [dealKey]: {
                    ...prev[userKey]?.[dealKey],
                    [sectionId]: expanded,
                },
            },
        }));
    };

    const toggleSectionExpanded = (sectionId: number) => {
        setSectionExpanded(sectionId, !isSectionExpanded(sectionId));
    };

    const sectionSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const persistSectionOrder = async (orderedIds: number[]) => {
        const positionById = new Map(sectionData.map(section => [section.id, section.position]));
        const updates = orderedIds
            .map((id, index) => ({ id, position: index }))
            .filter(({ id, position }) => positionById.get(id) !== position);
        try {
            await Promise.all(updates.map(({ id, position }) => updateSectionAsync({
                orgId: selectedOrganizationId, dealId, sectionId: id, patchData: { position },
            })));
        } catch {
            showError('Failed to reorder sections');
        } finally {
            invalidateSections();
        }
    };

    const handleSectionDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = orderedSectionIds.indexOf(active.id as number);
        const newIndex = orderedSectionIds.indexOf(over.id as number);
        if (oldIndex === -1 || newIndex === -1) return;

        const newOrder = arrayMove(orderedSectionIds, oldIndex, newIndex);
        setOrderedSectionIds(newOrder);
        persistSectionOrder(newOrder);
    };

    const handleAddNewRow = (sectionId?: number) => {
        createTimeline(
            {
                orgId: selectedOrganizationId,
                dealId,
                postData: { name: NEW_ACTIVITY_NAME, ...(sectionId !== undefined && { sectionId }) },
            },
            {
                onSuccess: (created: TimelineItem) => {
                    invalidateTimelines();
                    if (sectionId !== undefined) setSectionExpanded(sectionId, true);
                    openActivity(created.id);
                    setEditMode(true);
                },
                onError: () => showError('Failed to create activity'),
            },
        );
    };

    const handleDuplicate = (timeline: TimelineItem) => {
        duplicateTimeline(
            { orgId: selectedOrganizationId, dealId, timelineId: timeline.id },
            {
                onSuccess: (newTimeline) => {
                    // Optimistically append to every cached timelines query variant so the
                    // slideout can resolve the new id before the refetch lands.
                    queryClient.setQueriesData<TimelineItem[]>(
                        { queryKey: ['timelines', selectedOrganizationId, dealId] },
                        old => (old ? [...old, newTimeline] : [newTimeline]),
                    );
                    invalidateTimelines();
                    openSlideout(newTimeline.id);
                },
                onError: () => showError('Failed to duplicate activity'),
            },
        );
    };

    const handleRequestDelete = (timeline: TimelineItem) => setPendingDeleteTimeline(timeline);

    const handleConfirmDelete = () => {
        if (!pendingDeleteTimeline) return;
        const deletedId = pendingDeleteTimeline.id;
        deleteTimeline(
            { orgId: selectedOrganizationId, dealId, timelineId: deletedId },
            {
                onSuccess: () => {
                    invalidateTimelines();
                    setPendingDeleteTimeline(null);
                    if (selectedTimelineId === deletedId) {
                        closeSlideout();
                    }
                },
                onError: () => showError('Failed to delete activity'),
            },
        );
    };

    const handleRenameSection = (section: TimelineSection, name: string) => {
        updateSection(
            {
                orgId: selectedOrganizationId, dealId, sectionId: section.id, patchData: { name },
            },
            {
                onSuccess: () => invalidateSections(),
                onError: () => showError('Failed to rename section'),
            },
        );
    };

    const handleConfirmDeleteSection = () => {
        if (!pendingDeleteSection) return;
        const deletedSectionId = pendingDeleteSection.id;
        deleteSection(
            { orgId: selectedOrganizationId, dealId, sectionId: deletedSectionId },
            {
                onSuccess: () => {
                    invalidateSections();
                    invalidateTimelines();
                    setPendingDeleteSection(null);
                    if (selectedTimeline?.sectionId === deletedSectionId) {
                        closeSlideout();
                    }
                },
                onError: () => showError('Failed to delete section'),
            },
        );
    };

    const renderActivityRow = (item: TimelineItem) => (
        <TimelineActivityRow
            key={item.id}
            dealId={dealId}
            item={item}
            onRowClick={(id) => openSlideout(id)}
            onAttachmentClick={(id) => openSlideout(id, ATTACHMENTS_TAB)}
            onCommentClick={(id) => openSlideout(id, COMMENTS_TAB)}
            onDuplicate={handleDuplicate}
            onRequestDelete={handleRequestDelete}
            latestComment={latestComments?.[Number(item.id)]?.bodyHtml ?? undefined}
            isCommentCellHovered={hoveredRowId === item.id}
            onCommentCellHover={() => setHoveredRowId(item.id)}
            onCommentCellLeave={() => setHoveredRowId(null)}
        />
    );

    return (
        <div className="px-2 pb-2">
            <div ref={listRef} className="TimelineTable__container">
                <NSTable className="TimelineTable" hover>
                    <RenderIf isTrue={ungroupedTimelines.length > 0 || sectionData.length > 0} isLoading={isLoadingTimelines || isLoadingSections}>
                        <colgroup>
                            <col className='TimelineTable__col-activity' />
                            <col className="TimelineTable__col-target" />
                            <col className="TimelineTable__col-completed" />
                            <col className="TimelineTable__col-assignees" />
                            <col className="TimelineTable__col-status" />
                            <col className="TimelineTable__col-comment-thread" />
                            <col className="TimelineTable__col-attachments" />
                            <col className="TimelineTable__col-actions" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th className="d-flex justify-content-between align-items-center">
                                    <span>Activity</span>
                                    <NSButton
                                        className="TimelineTable__add-btn font-weight-bold cursor--pointer heap-deal-dashboard-overview-add-activity"
                                        callback={() => handleAddNewRow()}
                                        ariaLabel="Add activity"
                                        iconOnly
                                        icon={IconPlus}
                                        color="link"
                                        disabled={isCreatingTimeline}
                                    />
                                </th>
                                <th>Target</th>
                                <th className="text-nowrap">Date completed</th>
                                <th>Assignees</th>
                                <th>Status</th>
                                <th className="text-nowrap">Latest comment thread</th>
                                <th />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {ungroupedTimelines.map(item => renderActivityRow(item))}
                        </tbody>
                        <DndContext
                            sensors={sectionSensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleSectionDragEnd}
                        >
                            <SortableContext items={orderedSectionIds} strategy={verticalListSortingStrategy}>
                                {orderedSections.map(section => {
                                    const sectionTimelines = timelinesBySection.get(section.id) ?? [];
                                    const completedCount = sectionTimelines.filter(item => item.status === TimelineStatus.COMPLETE).length;
                                    return (
                                        <TimelineSectionRow
                                            key={section.id}
                                            section={section}
                                            completedCount={completedCount}
                                            totalCount={sectionTimelines.length}
                                            isExpanded={isSectionExpanded(section.id)}
                                            onToggleExpand={() => toggleSectionExpanded(section.id)}
                                            onRename={name => handleRenameSection(section, name)}
                                            onAddActivity={() => handleAddNewRow(section.id)}
                                            onRequestDelete={() => setPendingDeleteSection(section)}
                                        >
                                            {sectionTimelines.map(item => renderActivityRow(item))}
                                        </TimelineSectionRow>
                                    );
                                })}
                            </SortableContext>
                        </DndContext>
                    </RenderIf>
                </NSTable>
            </div>
            <TimelineSlideout
                isOpen={selectedTimelineId !== null}
                timeline={selectedTimeline}
                activityTab={activityTab}
                onTabChange={setActivityTab}
                dealId={dealId}
                orgId={selectedOrganizationId}
                editMode={editMode}
                setEditMode={setEditMode}
                attachmentsCount={selectedTimeline?.attachmentCount ?? 0}
                onClose={closeSlideout}
                onSwitchTimeline={openSlideout}
                onDuplicate={handleDuplicate}
                onRequestDelete={handleRequestDelete}
            />
            <ConfirmationModal
                isOpen={pendingDeleteTimeline !== null}
                danger
                toggle={() => setPendingDeleteTimeline(null)}
                onCancel={() => setPendingDeleteTimeline(null)}
                onConfirm={handleConfirmDelete}
                modalHeaderText={`Delete "${pendingDeleteTimeline?.name ?? ''}"?`}
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
            >
                <p>Are you sure you want to delete this activity? This action cannot be undone.</p>
            </ConfirmationModal>
            <ConfirmationModal
                isOpen={pendingDeleteSection !== null}
                danger
                toggle={() => setPendingDeleteSection(null)}
                onCancel={() => setPendingDeleteSection(null)}
                onConfirm={handleConfirmDeleteSection}
                modalHeaderText={`Delete "${pendingDeleteSection?.name ?? ''}"?`}
                modalFooterButtonText="Delete"
                modalFooterButtonColor="danger"
            >
                <p>
                    Are you sure you want to delete this section? All of its activities will be deleted,
                    but their documents will remain in the document library. This action cannot be undone.
                </p>
            </ConfirmationModal>
        </div>
    );
};

export default Timeline;
